
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageSquare, Phone, MoreVertical, Edit, Trash2, Clock, CheckCircle2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Lead } from '@/hooks/useLeadsData';
import { formatarTelefone } from './phoneUtils';

/**
 * Componente de lista de contatos compacta para mobile.
 * 
 * Onde usa: ContactsTable (renderizado condicionalmente em telas pequenas)
 * Contexto: Exibe leads em formato de lista simples (estilo WhatsApp) para economizar espaço.
 * Autor: IA
 * Data: 01/02/2026
 */

interface ContactsMobileListProps {
    leads: Lead[];
    onViewLead: (lead: Lead) => void;
    onEditLead: (lead: Lead) => void;
    onDeleteLead: (leadId: string) => void;
    onOpenChat: (lead: Lead) => void;
    onScheduleMessage: (lead: Lead) => void;
}

export const ContactsMobileList: React.FC<ContactsMobileListProps> = ({
    leads,
    onViewLead,
    onEditLead,
    onDeleteLead,
    onOpenChat,
    onScheduleMessage
}) => {

    const getInitials = (name?: string | null) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const getAvatarColor = (lead: Lead) => {
        if (lead.convertido) return 'bg-green-100 text-green-700';
        if (lead.origem_lead?.toLowerCase().includes('instagram')) return 'bg-purple-100 text-purple-700';
        return 'bg-gray-100 text-gray-700';
    };

    if (leads.length === 0) {
        return (
            <div className="text-center py-12 px-4">
                <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium">Nenhum contato encontrado</p>
                <p className="text-xs text-gray-400 mt-1">Tente ajustar os filtros</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100 pb-20">
            {leads.map((lead) => (
                <div
                    key={lead.id}
                    className="flex items-center justify-between p-3 active:bg-gray-50 transition-colors"
                >
                    {/* Esquerda: Avatar + Info */}
                    <div
                        className="flex items-center gap-3 overflow-hidden flex-1 cursor-pointer"
                        onClick={() => onViewLead(lead)}
                    >
                        {/* Avatar / Iniciais */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${getAvatarColor(lead)}`}>
                            {getInitials(lead.nome)}
                        </div>

                        {/* Texto */}
                        <div className="min-w-0 flex-1 mr-2">
                            <div className="flex items-center gap-1.5">
                                <h3 className="font-medium text-gray-900 truncate text-sm">
                                    {lead.nome || 'Sem nome'}
                                </h3>
                                {lead.convertido && (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 fill-green-50" />
                                )}
                            </div>

                            <div className="flex items-center text-xs text-gray-500 truncate">
                                {lead.telefone ? (
                                    <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {formatarTelefone(lead.telefone)}
                                    </span>
                                ) : (
                                    <span>{lead.email || 'Sem contato'}</span>
                                )}
                                {lead.origem_lead && (
                                    <>
                                        <span className="mx-1.5">•</span>
                                        <span className="capitalize">{lead.origem_lead}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Direita: Ações */}
                    <div className="flex items-center gap-1 shrink-0">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenChat(lead);
                            }}
                        >
                            <MessageSquare className="w-5 h-5" />
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-gray-400 hover:text-gray-600 rounded-full"
                                >
                                    <MoreVertical className="w-5 h-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => onViewLead(lead)}>
                                    Ver Detalhes
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onEditLead(lead)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onScheduleMessage(lead)} className="text-blue-600">
                                    <Clock className="w-4 h-4 mr-2" />
                                    Agendar Msg
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onDeleteLead(lead.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Excluir
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            ))}
        </div>
    );
};
